import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { toJS } from "mobx";
import { transcribeBlobViaRealtime } from "./floating-agent-blob-transcriber";
import { FloatingAgentPendingVoiceManager } from "./floating-agent-pending-voice-manager";
import { FloatingAgentVoice, type FloatingAgentVoiceResult } from "./floating-agent-voice";
import type { FloatingAgentVoiceSessionNoticeState } from "./floating-agent-voice-session-notice";
import {
  buildExternalMentionsContext,
  FloatingAgentComposerRegistry,
} from "./floating-agent-composer-mentions";
import { playPttEndSound, playPttStartSound } from "./floating-agent-sounds";
import { AppStore } from "@/store/AppStore";
import { FloatingAgentPageStore } from "@/store/pages/FloatingAgentPageStore/FloatingAgentPageStore";
import { MDSComboboxMention } from "@/mds/components/forms";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { electronModule } from "@/modules/electron";
import type { FloatingAgentVoiceMode } from "@/modules/electron/types";
import { logger } from "@/modules/logger";

export type FloatingAgentVoiceNotice =
  | "mic-denied"
  | "mic-unavailable"
  | "transcription-failed";

const MEM_AGENT_PTT_ANALYTICS_TRANSCRIPT_MAX_LENGTH = 1_000;

type FloatingAgentVoiceCaptureAnalytics = {
  voiceTurnId: number;
  voiceSessionId?: number;
  mode: FloatingAgentVoiceMode | null;
  startedAtMs: number | null;
  pendingEnd: {
    result: FloatingAgentVoiceResult;
    endedAtMs: number;
  } | null;
  didTrackStart: boolean;
  didTrackEnd: boolean;
};

/**
 * The push-to-talk hold lifecycle, shared by the widget hosts. The desktop
 * host drives it from the global-key IPC events (voice-hold-start /
 * -confirmed / -end); the web host drives the same calls from an in-page
 * press-and-hold. Owns the capture engine, the pending-voice queue, the
 * PTT sounds, and the post-release send (chips + foreground notes as
 * context, open thread as the reply destination).
 */
export const useFloatingAgentVoiceHold = ({
  store,
  floatingAgentPage,
  composerRegistryRef,
  foregroundMentionsRef,
  onExpandRequest,
}: {
  store: AppStore;
  floatingAgentPage: FloatingAgentPageStore;
  composerRegistryRef: MutableRefObject<FloatingAgentComposerRegistry>;
  foregroundMentionsRef: MutableRefObject<MDSComboboxMention[]>;
  /** Opens the panel (voice failure explanations and the quota upsell land there). */
  onExpandRequest: () => void;
}) => {
  const [voice] = useState(
    () =>
      new FloatingAgentVoice({
        getCustomVocabulary: () => toJS(store.spaceAccountSettings.customVocabulary),
      })
  );
  const [voiceNotice, setVoiceNotice] = useState<FloatingAgentVoiceNotice | null>(null);
  const [pendingVoice] = useState(
    () =>
      new FloatingAgentPendingVoiceManager({
        repository: store.floatingAgent.pendingVoiceRepository,
        onTranscriptReady: content =>
          floatingAgentPage.sendUserMessage({
            content,
            source: "voice",
          }),
        onClipDropped: () => setVoiceNotice("transcription-failed"),
        transcribe: blob =>
          transcribeBlobViaRealtime(blob, {
            customVocabulary: toJS(store.spaceAccountSettings.customVocabulary),
          }),
      })
  );
  const [voiceSessionNotice, setVoiceSessionNotice] =
    useState<FloatingAgentVoiceSessionNoticeState | null>(null);
  const [showSendConfirmation, setShowSendConfirmation] = useState(false);
  const [voiceTurnCompletionId, setVoiceTurnCompletionId] = useState(0);
  const [isVoiceTurnActive, setIsVoiceTurnActive] = useState(false);
  const confirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeVoiceTurnIdRef = useRef<number | null>(null);
  const activeVoiceCaptureAnalyticsRef = useRef<FloatingAgentVoiceCaptureAnalytics | null>(null);
  const nextVoiceTurnIdRef = useRef(1);
  const recoverableVoiceTurnRef = useRef<{
    resultPromise: Promise<FloatingAgentVoiceResult>;
    voiceTurnId: number;
    captureAnalytics: FloatingAgentVoiceCaptureAnalytics | null;
    captureEndedAtMs: number;
  } | null>(null);

  useEffect(
    () => () => {
      if (confirmationTimerRef.current) clearTimeout(confirmationTimerRef.current);
    },
    []
  );

  const completeVoiceTurn = useCallback((voiceTurnId: number) => {
    if (activeVoiceTurnIdRef.current !== voiceTurnId) return;
    activeVoiceTurnIdRef.current = null;
    setIsVoiceTurnActive(false);
    setVoiceTurnCompletionId(current => current + 1);
  }, []);

  const flushVoiceCaptureAnalytics = useCallback(
    (analytics: FloatingAgentVoiceCaptureAnalytics) => {
      const { mode, startedAtMs } = analytics;
      if (!mode || startedAtMs === null) return;

      if (!analytics.didTrackStart) {
        analytics.didTrackStart = true;
        trackEvent(TrackedEvent.MemAgentPttStart, { mode });
      }

      if (!analytics.pendingEnd || analytics.didTrackEnd) return;
      analytics.didTrackEnd = true;

      const { result, endedAtMs } = analytics.pendingEnd;
      trackEvent(TrackedEvent.MemAgentPttEnd, {
        mode,
        result: result.kind,
        transcript:
          result.kind === "transcript"
            ? result.text.slice(0, MEM_AGENT_PTT_ANALYTICS_TRANSCRIPT_MAX_LENGTH)
            : "",
        duration: Math.max(0, endedAtMs - startedAtMs) / 1_000,
      });

      if (activeVoiceCaptureAnalyticsRef.current === analytics) {
        activeVoiceCaptureAnalyticsRef.current = null;
      }
    },
    []
  );

  const trackVoiceCaptureEnd = useCallback(
    ({
      analytics,
      result,
      endedAtMs,
    }: {
      analytics: FloatingAgentVoiceCaptureAnalytics | null;
      result: FloatingAgentVoiceResult;
      endedAtMs: number;
    }) => {
      if (!analytics || analytics.didTrackEnd) return;
      analytics.pendingEnd = { result, endedAtMs };
      flushVoiceCaptureAnalytics(analytics);
    },
    [flushVoiceCaptureAnalytics]
  );

  // Brief checkmark on the circle after a voice message goes out. Completion
  // is scoped to the turn that scheduled it so an older timer cannot dismiss
  // a newer PTT gesture that reused the same presentation lease.
  const playSendConfirmation = useCallback(
    (voiceTurnId: number) => {
      if (activeVoiceTurnIdRef.current !== voiceTurnId) return;
      if (confirmationTimerRef.current) clearTimeout(confirmationTimerRef.current);
      setShowSendConfirmation(true);
      confirmationTimerRef.current = setTimeout(() => {
        confirmationTimerRef.current = null;
        if (activeVoiceTurnIdRef.current !== voiceTurnId) return;
        setShowSendConfirmation(false);
        completeVoiceTurn(voiceTurnId);
      }, 900);
    },
    [completeVoiceTurn]
  );

  const startVoiceTurn = useCallback((): number => {
    if (confirmationTimerRef.current) clearTimeout(confirmationTimerRef.current);
    confirmationTimerRef.current = null;
    setShowSendConfirmation(false);

    const voiceTurnId = nextVoiceTurnIdRef.current;
    nextVoiceTurnIdRef.current += 1;
    activeVoiceTurnIdRef.current = voiceTurnId;
    activeVoiceCaptureAnalyticsRef.current = null;
    setIsVoiceTurnActive(true);
    return voiceTurnId;
  }, []);

  const deliverVoiceResult = useCallback(
    async (result: FloatingAgentVoiceResult, voiceTurnId: number): Promise<boolean> => {
      if (result.kind === "transcript") {
        // With the panel open, voice behaves like the visible pane's composer:
        // chips + foreground notes ride along as context (and chips clear like
        // a typed send), and an open thread is the reply destination. In
        // circle mode (no composer mounted) the foreground notes still attach.
        const composer = composerRegistryRef.current.thread ?? composerRegistryRef.current.main;
        const sent = await floatingAgentPage.sendUserMessage({
          content: result.text,
          source: "voice",
          context: composer
            ? composer.buildContext()
            : buildExternalMentionsContext(foregroundMentionsRef.current),
          replyTo: composer?.getReplyTo?.(),
        });
        if (sent) {
          playSendConfirmation(voiceTurnId);
          composer?.clearMentions();
        }
        return sent;
      }

      if (result.kind === "audio-pending") {
        await pendingVoice.enqueue({
          blob: result.blob,
          mimeType: result.mimeType,
        });
        playSendConfirmation(voiceTurnId);
        return true;
      }

      return false;
    },
    [
      composerRegistryRef,
      floatingAgentPage,
      foregroundMentionsRef,
      pendingVoice,
      playSendConfirmation,
    ]
  );

  const finishVoiceResult = useCallback(
    async (result: FloatingAgentVoiceResult, voiceTurnId: number) => {
      let willShowConfirmation = false;
      try {
        willShowConfirmation = await deliverVoiceResult(result, voiceTurnId);
      } finally {
        if (!willShowConfirmation) completeVoiceTurn(voiceTurnId);
      }
    },
    [completeVoiceTurn, deliverVoiceResult]
  );

  // Boot-time resume: clips recorded offline before a restart transcribe once
  // we're online, without surfacing speculative chat messages.
  useEffect(() => {
    void pendingVoice.restore();
    return () => pendingVoice.dispose();
  }, [pendingVoice]);

  /**
   * The three MEM-19159 dead-PTT theories separate on these fields: a
   * blocked log with quotaReady=false proves the stranded counter; a blocked
   * log with allowed=false is a real quota stop; no renderer log at all for
   * a press the main process logged means the event never arrived.
   */
  const readMessageGateState = useCallback(
    () => ({
      inputDisabled: floatingAgentPage.isNativeChatMessageInputDisabled,
      quotaReady: floatingAgentPage.isNativeChatMessageQuotaReady,
      allowed: floatingAgentPage.nativeChatMessageFeatureGate.allowed,
      used: floatingAgentPage.nativeChatMessageFeatureGate.used,
      limit: floatingAgentPage.nativeChatMessageFeatureGate.limit,
    }),
    [floatingAgentPage]
  );

  /** Bare keydown feedback. Capture waits for a confirmed hold or second tap. */
  const startVoiceHold = useCallback(() => {
    // Quota exhausted: don't show a voice state for a capture that could
    // never send. The upsell still waits for gesture confirmation.
    if (floatingAgentPage.isNativeChatMessageInputDisabled) {
      logger.warn({
        message: "[FloatingAgentVoiceHold] voice hold blocked by the message gate",
        info: readMessageGateState(),
      });
      return;
    }
    logger.info({
      message: "[FloatingAgentVoiceHold] voice-hold-start received",
    });
    recoverableVoiceTurnRef.current = null;
    startVoiceTurn();
    setVoiceSessionNotice(null);
    voice.beginPending();
  }, [floatingAgentPage, readMessageGateState, startVoiceTurn, voice]);

  /**
   * The press crossed the hold threshold or claimed a first tap. Only this
   * confirmation activates the microphone.
   */
  const confirmVoiceHold = useCallback(
    ({
      voiceSessionId,
      mode,
    }: {
      voiceSessionId?: number;
      mode?: FloatingAgentVoiceMode;
    } = {}) => {
      const voiceTurnId = activeVoiceTurnIdRef.current;
      const reportCaptureFailed = () => {
        if (voiceSessionId === undefined) return;
        electronModule.floatingAgent.voiceCaptureFailed({ voiceSessionId });
      };

      if (floatingAgentPage.isNativeChatMessageInputDisabled) {
        logger.warn({
          message: "[FloatingAgentVoiceHold] voice capture blocked by the message gate",
          info: readMessageGateState(),
        });
        reportCaptureFailed();
        void floatingAgentPage.openNativeChatLimitUpsellForCurrentGate();
        onExpandRequest();
        return;
      }
      floatingAgentPage.completeFirstMessageOnboardingIfNeeded("voice-hold-confirmed");
      logger.info({
        message: "[FloatingAgentVoiceHold] voice-hold-confirmed received",
        info: { voiceSessionId: voiceSessionId ?? null, mode: mode ?? null },
      });

      const captureAnalytics: FloatingAgentVoiceCaptureAnalytics | null =
        voiceTurnId === null
          ? null
          : {
              voiceTurnId,
              voiceSessionId,
              mode: mode ?? null,
              startedAtMs: null,
              pendingEnd: null,
              didTrackStart: false,
              didTrackEnd: false,
            };
      activeVoiceCaptureAnalyticsRef.current = captureAnalytics;

      void voice
        .start({
          onCaptureStarted: () => {
            if (
              !captureAnalytics ||
              activeVoiceTurnIdRef.current !== voiceTurnId ||
              activeVoiceCaptureAnalyticsRef.current !== captureAnalytics
            ) {
              return;
            }

            captureAnalytics.startedAtMs = performance.now();
            flushVoiceCaptureAnalytics(captureAnalytics);
          },
        })
        .then(result => {
          if (result.ok) {
            // Cue on actual recording start, not press time — capture opens
            // with real latency, and a press-time ding invites speech the
            // recorder isn't hearing yet.
            playPttStartSound();
            setVoiceNotice(null);
            return;
          }
          // The hold ended before the mic opened — nothing was recorded and
          // nothing needs explaining.
          if (result.canceled) {
            if (voiceTurnId !== null) completeVoiceTurn(voiceTurnId);
            return;
          }
          reportCaptureFailed();
          // Any can't-start failure routes to the composer with an
          // explanation — one fallback path, cause-specific copy.
          logger.warn({
            message: "[FloatingAgentVoiceHold] voice hold could not start",
            info: { failure: result.failure },
          });
          setVoiceNotice(result.failure);
          onExpandRequest();
        });
    },
    [
      completeVoiceTurn,
      floatingAgentPage,
      flushVoiceCaptureAnalytics,
      readMessageGateState,
      voice,
      onExpandRequest,
    ]
  );

  /** Finalizes whether a speculative second press became PTT or hands-free. */
  const resolveVoiceMode = useCallback(
    ({ voiceSessionId, mode }: { voiceSessionId: number; mode: FloatingAgentVoiceMode }) => {
      const analytics = activeVoiceCaptureAnalyticsRef.current;
      if (!analytics || analytics.voiceSessionId !== voiceSessionId) return;

      analytics.mode = mode;
      flushVoiceCaptureAnalytics(analytics);
    },
    [flushVoiceCaptureAnalytics]
  );

  // PTT release stays in circle mode: the checkmark confirms the send, and
  // the reply arrives as a peek bubble.
  const endVoiceHold = useCallback(
    async ({ canceled = false } = {}) => {
      const voiceTurnId = activeVoiceTurnIdRef.current;
      const captureAnalytics =
        activeVoiceCaptureAnalyticsRef.current?.voiceTurnId === voiceTurnId
          ? activeVoiceCaptureAnalyticsRef.current
          : null;
      const captureEndedAtMs = performance.now();
      recoverableVoiceTurnRef.current = null;
      setVoiceSessionNotice(null);

      // A timed-out tap or shortcut combo is not a voice turn. Tear down its
      // pending UI (or a second-tap capture canceled by a combo) immediately.
      if (canceled) {
        voice.cancel();
        trackVoiceCaptureEnd({
          analytics: captureAnalytics,
          result: { kind: "empty" },
          endedAtMs: captureEndedAtMs,
        });
        if (voiceTurnId !== null) completeVoiceTurn(voiceTurnId);
        return;
      }

      // The end cue must not enter the final microphone frames. Its audible
      // tone overlaps the local pipeline-drain window after keyup, so play it
      // only once the worklet has acknowledged the capture boundary.
      const shouldPlayEndSound = voice.isListening;
      const result = await voice.stop({
        onCaptureSealed: shouldPlayEndSound ? playPttEndSound : undefined,
      });
      trackVoiceCaptureEnd({
        analytics: captureAnalytics,
        result,
        endedAtMs: captureEndedAtMs,
      });
      if (voiceTurnId !== null) {
        await finishVoiceResult(result, voiceTurnId);
      }
    },
    [completeVoiceTurn, finishVoiceResult, trackVoiceCaptureEnd, voice]
  );

  /**
   * Seals an Escape-canceled open-mic turn but keeps its eventual result in
   * memory until Electron either recovers or expires the five-second window.
   */
  const cancelOpenMic = useCallback(
    ({
      countdownStartedAtMs,
      countdownDeadlineMs,
    }: {
      countdownStartedAtMs: number;
      countdownDeadlineMs: number;
    }) => {
      setVoiceSessionNotice({
        kind: "canceled",
        countdownStartedAtMs,
        countdownDeadlineMs,
      });

      const shouldPlayEndSound = voice.isListening;
      const voiceTurnId = activeVoiceTurnIdRef.current;
      if (voiceTurnId === null) return;
      const captureAnalytics =
        activeVoiceCaptureAnalyticsRef.current?.voiceTurnId === voiceTurnId
          ? activeVoiceCaptureAnalyticsRef.current
          : null;
      const captureEndedAtMs = performance.now();
      const resultPromise = voice.stop({
        onCaptureSealed: shouldPlayEndSound ? playPttEndSound : undefined,
      });

      recoverableVoiceTurnRef.current = {
        resultPromise,
        voiceTurnId,
        captureAnalytics,
        captureEndedAtMs,
      };
    },
    [voice]
  );

  /**
   * Commits a recoverable cancellation. Recovery can arrive before final
   * transcription; awaiting the retained result preserves the full tail.
   */
  const recoverCanceledOpenMic = useCallback(async () => {
    const recoverableTurn = recoverableVoiceTurnRef.current;
    if (!recoverableTurn) return;

    recoverableVoiceTurnRef.current = null;
    setVoiceSessionNotice(null);
    const result = await recoverableTurn.resultPromise;
    trackVoiceCaptureEnd({
      analytics: recoverableTurn.captureAnalytics,
      result,
      endedAtMs: recoverableTurn.captureEndedAtMs,
    });
    await finishVoiceResult(result, recoverableTurn.voiceTurnId);
  }, [finishVoiceResult, trackVoiceCaptureEnd]);

  const expireCanceledOpenMic = useCallback(() => {
    const recoverableTurn = recoverableVoiceTurnRef.current;
    recoverableVoiceTurnRef.current = null;
    setVoiceSessionNotice(current => (current?.kind === "canceled" ? null : current));
    if (!recoverableTurn) return;

    trackVoiceCaptureEnd({
      analytics: recoverableTurn.captureAnalytics,
      result: { kind: "empty" },
      endedAtMs: recoverableTurn.captureEndedAtMs,
    });
    completeVoiceTurn(recoverableTurn.voiceTurnId);
  }, [completeVoiceTurn, trackVoiceCaptureEnd]);

  const showOpenMicEndingSoon = useCallback(
    ({
      countdownStartedAtMs,
      countdownDeadlineMs,
    }: {
      countdownStartedAtMs: number;
      countdownDeadlineMs: number;
    }) => {
      setVoiceSessionNotice({
        kind: "ending-soon",
        countdownStartedAtMs,
        countdownDeadlineMs,
      });
    },
    []
  );

  const clearOpenMicEndingSoon = useCallback(() => {
    setVoiceSessionNotice(current => (current?.kind === "ending-soon" ? null : current));
  }, []);

  return {
    voice,
    pendingVoice,
    voiceNotice,
    setVoiceNotice,
    voiceSessionNotice,
    showSendConfirmation,
    voiceTurnCompletionId,
    isVoiceTurnActive,
    startVoiceHold,
    confirmVoiceHold,
    resolveVoiceMode,
    endVoiceHold,
    cancelOpenMic,
    recoverCanceledOpenMic,
    expireCanceledOpenMic,
    showOpenMicEndingSoon,
    clearOpenMicEndingSoon,
  };
};
